import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, ChevronDown, MessageCircle, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CrossSiteNavMobile } from "@/components/CrossSiteNav";
import EclipseLogo from "@/components/EclipseLogo";

const WA_RAW = "528116289267";
const TEL_RAW = "528189899822";
const TEL_DISPLAY = "+52 81 8989 9822";
const WHATSAPP_URL = "https://wa.me/" + WA_RAW + "?text=" + encodeURIComponent("Hola, me interesa cotizar un proyecto con Eclipse Cubiertas.");

const FLEXSHADE_SUB = [
    { label: "Walk", to: "/flexshade#walk" },
    { label: "Curve", to: "/flexshade#curve" },
    { label: "Curve Corto", to: "/flexshade#curve-corto" },
    { label: "Sun", to: "/flexshade#sun" },
    { label: "Back to Back", to: "/flexshade#back-to-back" },
];

const SKYCOVER_SUB = [
    { label: "Malla Sombra", to: "/velarias#malla-sombra" },
    { label: "Membrana PVC", to: "/velarias#membrana-pvc" },
    { label: "Canchas Deportivas", to: "/velarias#canchas" },
    { label: "Industrial", to: "/velarias#industrial" },
];

export default function Navbar({ variant = "overlay" }) {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isSolid = variant === "solid" || scrolled;

    const handleNavTo = (to) => {
        setMobileOpen(false);
        navigate(to);
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isSolid ? "bg-black/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"}`}
        >
            <div className="container-eclipse flex items-center justify-between h-20 gap-4">
                <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 leading-none flex-shrink-0">
                    <EclipseLogo size="h-14" alt="Eclipse Cubiertas - Líderes en Cubiertas Textiles en México" />
                </Link>

                <nav className="hidden lg:flex items-center gap-6">
                    <NavLink to="/" label="INICIO" testId="navbar-inicio" />

                    <DropdownMenu>
                        <DropdownMenuTrigger data-testid="navbar-flexshade-trigger" className="flex items-center gap-1 text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">
                            FLEXSHADE <ChevronDown className="w-4 h-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="bg-black/95 backdrop-blur-md border-white/10 w-52">
                            {FLEXSHADE_SUB.map((it) => (
                                <DropdownMenuItem key={it.label} data-testid={`navbar-flexshade-${it.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => handleNavTo(it.to)} className="cursor-pointer font-body text-white hover:text-eclipse-cta focus:text-eclipse-cta focus:bg-white/5">
                                    {it.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <DropdownMenu>
                        <DropdownMenuTrigger data-testid="navbar-skycover-trigger" className="flex items-center gap-1 text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">
                            SKYCOVER <ChevronDown className="w-4 h-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="bg-black/95 backdrop-blur-md border-white/10 w-52">
                            {SKYCOVER_SUB.map((it) => (
                                <DropdownMenuItem key={it.label} data-testid={`navbar-skycover-${it.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => handleNavTo(it.to)} className="cursor-pointer font-body text-white hover:text-eclipse-cta focus:text-eclipse-cta focus:bg-white/5">
                                    {it.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <a href={location.pathname === "/" ? "#proyectos" : "/#proyectos"} data-testid="navbar-proyectos" className="text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">PROYECTOS</a>
                    <a href="#footer" data-testid="navbar-contacto" className="text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">CONTACTO</a>
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    <a href={`tel:+${TEL_RAW}`} data-testid="navbar-phone-btn" aria-label="Llamar a Eclipse Cubiertas" title={TEL_DISPLAY} className="hidden md:inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-sm font-display font-bold uppercase tracking-wider border border-white/30 text-white hover:bg-white/10 transition-colors">
                        <Phone className="w-4 h-4" />
                        <span className="hidden xl:inline">{TEL_DISPLAY}</span>
                    </a>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="navbar-whatsapp-btn" className="btn-eclipse-cta hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm uppercase tracking-wider">
                        <MessageCircle className="w-4 h-4" />
                        Cotiza Ahora
                    </a>
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild data-testid="navbar-mobile-trigger">
                            <button aria-label="Abrir menú" className="lg:hidden p-2 rounded-md transition-colors text-white">
                                <Menu className="w-6 h-6" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-white w-80 p-0 overflow-y-auto">
                            <SheetHeader className="px-6 pt-6 pb-4 border-b border-neutral-200 flex flex-row items-center justify-between bg-eclipse-primary">
                                <SheetTitle asChild>
                                    <div><EclipseLogo size="h-10" alt="Eclipse Cubiertas" /></div>
                                </SheetTitle>
                                <SheetDescription className="sr-only">Menú de navegación de Eclipse Cubiertas</SheetDescription>
                            </SheetHeader>
                            <nav className="flex flex-col p-6 gap-1">
                                <CrossSiteNavMobile active="eclipse" onNavigate={() => setMobileOpen(false)} />
                                <MobileItem label="Inicio" onClick={() => handleNavTo("/")} testId="mobile-nav-inicio" />
                                <MobileItem label="FlexShade" onClick={() => handleNavTo("/flexshade")} testId="mobile-nav-flexshade" />
                                <MobileItem label="Skycover" onClick={() => handleNavTo("/velarias")} testId="mobile-nav-skycover" />
                                <MobileItem label="Proyectos" onClick={() => { setMobileOpen(false); if (location.pathname === "/") { document.getElementById("proyectos")?.scrollIntoView({ behavior: "smooth" }); } else { navigate("/#proyectos"); } }} testId="mobile-nav-proyectos" />
                                <MobileItem label="Contacto" onClick={() => { setMobileOpen(false); if (location.pathname === "/") { document.getElementById("footer")?.scrollIntoView({ behavior: "smooth" }); } }} testId="mobile-nav-contacto" />
                                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-whatsapp" className="btn-eclipse-cta mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm text-sm uppercase tracking-wider">
                                    <MessageCircle className="w-4 h-4" />
                                    Cotiza por WhatsApp
                                </a>
                                <a href={`tel:+${TEL_RAW}`} data-testid="mobile-nav-phone" className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm text-sm uppercase tracking-wider border border-eclipse-primary text-eclipse-primary hover:bg-eclipse-primary hover:text-white transition-colors font-display font-bold">
                                    <Phone className="w-4 h-4" />
                                    {TEL_DISPLAY}
                                </a>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}

function NavLink({ to, label, testId }) {
    return (
        <Link to={to} data-testid={testId} className="text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">
            {label}
        </Link>
    );
}

function MobileItem({ label, onClick, testId }) {
    return (
        <button onClick={onClick} data-testid={testId} className="w-full text-left py-3 px-2 font-display font-semibold text-base text-eclipse-primary hover:text-eclipse-cta hover:bg-neutral-50 rounded-md transition-colors">
            {label}
        </button>
    );
}
